import { useState } from "react"
import { Pencil, Check, X, Save } from "lucide-react"
import { updateUserProfile } from "@/lib/api"
import { useReadOnly } from "@/lib/read-only"
import { toast } from "sonner"

interface ProfileEditorProps {
  userId: string
  profile: Record<string, any>
  onSaved: (profile: Record<string, any>) => void
}

const ACTIVITY_OPTIONS = [
  "Weightlifting",
  "Running",
  "Swimming",
  "Yoga",
  "Pilates",
  "Sports",
  "Walking/Hiking",
  "Cycling",
  "HIIT",
  "Mobility",
]

function csvToArray(value: string): string[] {
  return value
    .split(",")
    .map((v) => v.trim())
    .filter(Boolean)
}

function arrayToCsv(value: unknown): string {
  if (!Array.isArray(value)) return ""
  return value
    .map((v) => (typeof v === "string" ? v : (v as { name?: string })?.name ?? ""))
    .filter(Boolean)
    .join(", ")
}

export function ProfileEditor({ userId, profile, onSaved }: ProfileEditorProps) {
  const { readOnly } = useReadOnly()
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)

  const [draft, setDraft] = useState<Record<string, any>>(() => ({
    first_name: profile.first_name ?? "",
    last_name: profile.last_name ?? "",
    age: profile.age ?? "",
    gender: profile.gender ?? "",
    location: profile.location ?? "",
    height_feet: profile.height_feet ?? "",
    height_inches: profile.height_inches ?? "",
    weight_lbs: profile.weight_lbs ?? "",
    selected_activities: arrayToCsv(profile.selected_activities),
    goals: arrayToCsv(profile.goals),
    profile_summary: profile.profile_summary ?? "",
  }))

  const startEditing = () => {
    setDraft({
      first_name: profile.first_name ?? "",
      last_name: profile.last_name ?? "",
      age: profile.age ?? "",
      gender: profile.gender ?? "",
      location: profile.location ?? "",
      height_feet: profile.height_feet ?? "",
      height_inches: profile.height_inches ?? "",
      weight_lbs: profile.weight_lbs ?? "",
      selected_activities: arrayToCsv(profile.selected_activities),
      goals: arrayToCsv(profile.goals),
      profile_summary: profile.profile_summary ?? "",
    })
    setEditing(true)
  }

  const toNumberOrNull = (raw: unknown): number | null => {
    if (raw === "" || raw === null || raw === undefined) return null
    const n = Number(raw)
    return Number.isFinite(n) ? n : null
  }

  const handleSave = async () => {
    // Enforce the project-wide max-2-goals rule client-side as well.
    const goals = csvToArray(draft.goals).slice(0, 2)
    if (csvToArray(draft.goals).length > 2) {
      toast.error("Profile may have at most 2 goals — extra ones were dropped")
    }

    setSaving(true)
    try {
      const patch: Record<string, any> = {
        first_name: draft.first_name?.trim() || null,
        last_name: draft.last_name?.trim() || null,
        age: toNumberOrNull(draft.age),
        gender: draft.gender?.trim() || null,
        location: draft.location?.trim() || null,
        height_feet: toNumberOrNull(draft.height_feet),
        height_inches: toNumberOrNull(draft.height_inches),
        weight_lbs: toNumberOrNull(draft.weight_lbs),
        selected_activities: csvToArray(draft.selected_activities),
        goals,
        profile_summary: draft.profile_summary?.trim() || null,
      }
      const updated = await updateUserProfile(userId, patch)
      onSaved(updated)
      setEditing(false)
      toast.success("Profile updated")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Save failed")
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        <button
          onClick={startEditing}
          disabled={readOnly}
          className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs hover:bg-accent disabled:opacity-50"
          title={readOnly ? "Read-only mode active" : "Edit profile"}
        >
          <Pencil className="size-3" />
          Edit profile
        </button>
      </div>
    )
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-sm">Edit Profile</h3>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setEditing(false)}
            disabled={saving}
            className="inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs hover:bg-accent disabled:opacity-50"
          >
            <X className="size-3" /> Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={readOnly || saving}
            className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1 text-xs text-white hover:bg-green-700 disabled:opacity-50"
          >
            {saving ? (
              <>Saving...</>
            ) : (
              <>
                <Check className="size-3" /> Save
              </>
            )}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        <Field label="First name">
          <input
            value={draft.first_name}
            onChange={(e) => setDraft((d) => ({ ...d, first_name: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
        </Field>
        <Field label="Last name">
          <input
            value={draft.last_name}
            onChange={(e) => setDraft((d) => ({ ...d, last_name: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
        </Field>
        <Field label="Age">
          <input
            type="number"
            value={draft.age}
            onChange={(e) => setDraft((d) => ({ ...d, age: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm"
            min={0}
          />
        </Field>
        <Field label="Gender">
          <input
            value={draft.gender}
            onChange={(e) => setDraft((d) => ({ ...d, gender: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
        </Field>
        <Field label="Location">
          <input
            value={draft.location}
            onChange={(e) => setDraft((d) => ({ ...d, location: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm"
          />
        </Field>
        <Field label="Height (ft / in)">
          <div className="flex gap-2">
            <input
              type="number"
              value={draft.height_feet}
              onChange={(e) => setDraft((d) => ({ ...d, height_feet: e.target.value }))}
              className="w-1/2 rounded-md border px-2 py-1 text-sm"
              min={0}
            />
            <input
              type="number"
              value={draft.height_inches}
              onChange={(e) => setDraft((d) => ({ ...d, height_inches: e.target.value }))}
              className="w-1/2 rounded-md border px-2 py-1 text-sm"
              min={0}
              max={11}
            />
          </div>
        </Field>
        <Field label="Weight (lbs)">
          <input
            type="number"
            value={draft.weight_lbs}
            onChange={(e) => setDraft((d) => ({ ...d, weight_lbs: e.target.value }))}
            className="w-full rounded-md border px-2 py-1 text-sm"
            min={0}
          />
        </Field>
      </div>

      <Field label="Activities (comma-separated)">
        <input
          value={draft.selected_activities}
          onChange={(e) => setDraft((d) => ({ ...d, selected_activities: e.target.value }))}
          className="w-full rounded-md border px-2 py-1 text-sm"
          placeholder={ACTIVITY_OPTIONS.slice(0, 4).join(", ") + ", …"}
        />
      </Field>

      <Field label="Goals (comma-separated, max 2 per Offseason rules)">
        <input
          value={draft.goals}
          onChange={(e) => setDraft((d) => ({ ...d, goals: e.target.value }))}
          className="w-full rounded-md border px-2 py-1 text-sm"
          placeholder="Build muscle, Improve endurance"
        />
      </Field>

      <Field label="Profile summary (free text)">
        <textarea
          rows={3}
          value={draft.profile_summary}
          onChange={(e) => setDraft((d) => ({ ...d, profile_summary: e.target.value }))}
          className="w-full rounded-md border px-2 py-1 text-sm font-mono"
        />
      </Field>

      <div className="flex justify-end pt-1">
        <button
          onClick={handleSave}
          disabled={readOnly || saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-green-600 px-3 py-1.5 text-sm text-white hover:bg-green-700 disabled:opacity-50"
        >
          <Save className="size-3.5" /> {saving ? "Saving..." : "Save changes"}
        </button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wide text-muted-foreground mb-1">
        {label}
      </span>
      {children}
    </label>
  )
}
